// src/components/public_utilities/GisMapView.js
// --- UPDATE (GIS map — road segment health layer) -----------------------------
//   New optional props (all default to the old behaviour, so other pages are unchanged):
//     roadSegments        { roads, segments, unsegmented } from GET /roads/segments/map
//     roadDisplayMode     'roads' (whole roads, as before) | 'segments' (each segment coloured by PCI)
//     segmentBands        Set of visible health bands (good / satisfactory / fair / poor / critical / unscored)
//     showSegmentLabels   PCI badge on every segment (always shown from zoom 16)
//     selectedSegmentId, onSegmentSelect(seg, road), onOpenRoadReport(road, seg)
//     showAssetMarkers / showSensorMarkers / showAnomalies   toggle the marker layers
//   In 'segments' mode the whole-road line of a sliced road is replaced by its segments; roads that
//   are not sliced yet are drawn dashed grey. A health legend with counts sits bottom-left.
// --- END UPDATE ---------------------------------------------------------------
// --- UPDATE (Public Utilities v3 — PU-only map layer) --------------------------
//   New optional props (default = old behaviour, so every other page that uses this map is unchanged):
//     puLayer            { features, zones, managers, legend, total, no_gps } from GET /public-utilities/map
//                        (expanded by publicUtilitiesApi.getMapLayer) — drawn on one canvas, clustered
//                        below zoom 15, never roads, never 0,0 points
//     puColorBy          'type' | 'status' | 'due' | 'manager'
//     puVisibleTypes     Set of asset types to draw (null = all)
//     showPuZones        constituency (= zone) polygons with counts (default true)
//     selectedPuId, onPuFeatureSelect(feature)      halo + click → Asset 360
//     puFitOnLoad        fit the map to the inventory the first time it loads (default true)
//     showPuLegend       legend with real counts (default true when puLayer is given)
//     liveConnected      the page's own socket state; overrides the NotificationContext banner
//   Performance: the debug logger no longer console.logs + JSON-rewrites sessionStorage on every
//   render (it did both synchronously); console output is development-only and storage is batched.
// --- END UPDATE ---------------------------------------------------------------
// --- UPDATE (roads v1.4 — road ponding forecast layer) -------------------------
//   New optional props (default off — other pages unchanged):
//     pondingForecast          GET /roads/ponding-forecast payload (segments with hourly depth)
//     showPonding              draw it (wet segments, settling points sized by depth, link to the drain)
//     pondingHour              forecast hour to show (null = peak)
//     selectedPondingSegmentId, onPondingSegmentSelect(seg), onPondingOpenReport(seg)
//   The layer lives INSIDE MapContainer (useMap), so it follows the map's own lifecycle; a compact legend with
//   band counts sits bottom-right. onMapReady(null) is now called when the map unmounts (no stale map handles).
// --- END UPDATE ---------------------------------------------------------------
import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { 
  MapContainer, 
  TileLayer, 
  useMap, 
  Polyline, 
  Popup, 
  CircleMarker, 
  Circle, 
  GeoJSON, 
  Tooltip as LeafletTooltip 
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import AssetMarkers from './AssetMarkers';
import SensorMarkers from './SensorMarkers';
import predictiveMaintenanceService from '../../services/predictiveMaintenanceService';
import './GisMapView.css';
import TechnicianMarkers from './TechnicianMarkers';

// --- UPDATE (GIS map — road segment health layer)
import RoadSegmentLayer from '../roads/RoadSegmentLayer';
// --- UPDATE (roads v1.4)
import RoadPondingMapLayer from '../roads/ponding/RoadPondingMapLayer';
import { BAND_KEYS as PONDING_BANDS, bandMeta as pondingBand, bandAt as pondingBandAt } from '../roads/ponding/pondingUi';
// --- END UPDATE
import { SEGMENT_BANDS, filterSegments, summarize } from '../roads/roadSegmentHealth';
// --- END UPDATE

// Global WebSocket context
import { useNotifications } from '../../contexts/NotificationContext';
// --- UPDATE (Public Utilities v3)
import PuAssetLayer from './pu/PuAssetLayer';
import { puLegend, PU_COLOR_BY } from './pu/puMapStyles';
// --- END UPDATE

// 4D Subsurface Clearance Service
import { spatialService, CLEARANCE_STATUS } from '../../services/spatialService';

// ============================================================================
// DEBUG UTILITY
// ============================================================================
class GisMapDebugger {
  constructor() {
    this.logs = [];
    this.maxLogs = 500;
    this.performanceMarks = new Map();
    this.renderCount = 0;
    this.eventCount = new Map();
  }

  log(level, action, data = {}) {
    const logEntry = { 
      timestamp: new Date().toISOString(), 
      level, 
      action, 
      component: 'GisMapView', 
      ...data, 
      renderCount: this.renderCount 
    };
    this.logs.push(logEntry);
    if (this.logs.length > this.maxLogs) this.logs.shift();

    const colors = { 
      INFO: '#2196F3', 
      SUCCESS: '#4CAF50', 
      WARNING: '#FF9800', 
      ERROR: '#F44336', 
      DEBUG: '#9C27B0', 
      PERFORMANCE: '#00BCD4' 
    };
    // --- UPDATE (v3): development-only console, batched storage (was sync parse+stringify on every log)
    if (process.env.NODE_ENV === 'development' || (typeof window !== 'undefined' && window.__DEBUG__)) {
      console.log(`%c[${level}] ${action}`, `color: ${colors[level]}; font-weight: bold;`, data);
    }
    if (this._flushTimer) return;
    this._flushTimer = setTimeout(() => {
      this._flushTimer = null;
      try { sessionStorage.setItem('gis_map_logs', JSON.stringify(this.logs.slice(-100))); } catch (e) { /* storage full / private mode */ }
    }, 1000);
    // --- END UPDATE
  }

  startPerformance(marker) { this.performanceMarks.set(marker, performance.now()); }
  endPerformance(marker, action) {
    const start = this.performanceMarks.get(marker);
    if (start) {
      const duration = performance.now() - start;
      this.log('PERFORMANCE', action, { duration: `${duration.toFixed(2)}ms`, marker });
      this.performanceMarks.delete(marker);
      return duration;
    }
    return null;
  }
  incrementRender() { this.renderCount++; }
  trackEvent(eventName) {
    const count = (this.eventCount.get(eventName) || 0) + 1;
    this.eventCount.set(eventName, count);
  }
  getStats() { 
    return { 
      totalLogs: this.logs.length, 
      renderCount: this.renderCount, 
      eventCounts: Object.fromEntries(this.eventCount), 
      recentLogs: this.logs.slice(-10) 
    }; 
  }
  exportLogs() {
    const dataBlob = new Blob([JSON.stringify(this.logs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a'); 
    link.href = url; 
    link.download = `gis-map-debug-${Date.now()}.json`; 
    link.click(); 
    URL.revokeObjectURL(url);
  }
  clearLogs() { this.logs = []; sessionStorage.removeItem('gis_map_logs'); this.log('INFO', 'logs_cleared'); }
}
const mapDebugger = new GisMapDebugger();
if (typeof window !== 'undefined') { 
  window.__GisMapDebug__ = { 
    getLogs: () => mapDebugger.logs, 
    getStats: () => mapDebugger.getStats(), 
    exportLogs: () => mapDebugger.exportLogs(), 
    clearLogs: () => mapDebugger.clearLogs() 
  }; 
}

// ============================================================================
// ERROR BOUNDARY
// ============================================================================
class MapErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null, errorInfo: null }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, errorInfo) {
    mapDebugger.log('ERROR', 'error_boundary_caught', { 
      error: error.message, 
      stack: error.stack, 
      componentStack: errorInfo.componentStack 
    });
    this.setState({ error, errorInfo });
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="map-error-container">
          <div className="map-error-content">
            <h3>Map Loading Error</h3>
            <p>The GIS map canvas encountered a rendering conflict.</p>
            <button 
              onClick={() => { this.setState({ hasError: false, error: null, errorInfo: null }); window.location.reload(); }} 
              className="btn btn-primary"
            >
              Reload Map
            </button>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{ marginTop: '20px', textAlign: 'left' }}>
                <summary>Error Details</summary>
                <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                  {this.state.error.toString()}{this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ============================================================================
// MAP INITIALIZER & RESIZE CONTROLLERS
// ============================================================================
const MapInitializer = ({ onMapReady }) => {
  const map = useMap();
  const initializedRef = useRef(false);
  // --- UPDATE (roads v1.4): tell the page when this map goes away (tab switch / remount)
  const readyRef = useRef(onMapReady);
  readyRef.current = onMapReady;
  useEffect(() => () => { if (initializedRef.current) { try { readyRef.current?.(null); } catch (_) { /* page gone */ } } }, []);
  // --- END UPDATE
  useEffect(() => {
    if (!initializedRef.current && map) {
      mapDebugger.log('INFO', 'map_initialized');
      initializedRef.current = true;
      if (onMapReady) onMapReady(map);
    }
  }, [map, onMapReady]);
  return null;
};

// --- UPDATE (roads v1.4): ponding layer bound to this MapContainer's map
const PondingLayerBridge = (props) => {
  const map = useMap();
  return <RoadPondingMapLayer map={map} {...props} />;
};
// --- END UPDATE

const MapResizeController = () => {
  const map = useMap();
  useEffect(() => {
    const handleResize = () => {
      map.invalidateSize();
    };

    handleResize();
    const t1 = setTimeout(handleResize, 150);
    const t2 = setTimeout(handleResize, 400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [map]);
  return null;
};

const MapBoundsController = memo(({ assets, sensors, bounds, onBoundsCalculated }) => {
  const map = useMap();
  const hasCalculatedRef = useRef(false);
  useEffect(() => {
    mapDebugger.log('DEBUG', 'bounds_controller_mount');
    if (bounds && !hasCalculatedRef.current) {
      try {
        mapDebugger.startPerformance('fit_bounds');
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
        mapDebugger.endPerformance('fit_bounds', 'map_bounds_fitted');
        hasCalculatedRef.current = true;
      } catch (error) { 
        mapDebugger.log('ERROR', 'bounds_fit_failed', { error: error.message }); 
      }
    } else if (!bounds && (assets.length > 0 || sensors.length > 0)) {
      try {
        mapDebugger.startPerformance('calculate_bounds');
        const allPoints = [];
        
        assets.forEach(a => {
          if (a.latitude && a.longitude && !isNaN(a.latitude) && !isNaN(a.longitude)) {
            allPoints.push([parseFloat(a.latitude), parseFloat(a.longitude)]);
          } else if (a.geo_point && a.geo_point.coordinates) {
            if (a.geo_point.type === 'Point') {
              allPoints.push([a.geo_point.coordinates[1], a.geo_point.coordinates[0]]);
            } else if (a.geo_point.type === 'LineString') {
              a.geo_point.coordinates.forEach(coord => allPoints.push([coord[1], coord[0]]));
            } else if (a.geo_point.type === 'MultiLineString') {
              a.geo_point.coordinates.flat(1).forEach(coord => allPoints.push([coord[1], coord[0]]));
            }
          }
        });

        sensors.forEach(s => {
          if (s.latitude && s.longitude && !isNaN(s.latitude) && !isNaN(s.longitude)) {
            allPoints.push([parseFloat(s.latitude), parseFloat(s.longitude)]);
          }
        });

        if (allPoints.length > 0) {
          const newBounds = L.latLngBounds(allPoints);
          map.fitBounds(newBounds, { padding: [50, 50], maxZoom: 16 });
          if (onBoundsCalculated) onBoundsCalculated(newBounds);
          const duration = mapDebugger.endPerformance('calculate_bounds', 'bounds_calculated');
          mapDebugger.log('SUCCESS', 'bounds_calculated', { pointCount: allPoints.length, duration: `${duration.toFixed(2)}ms` });
        }
      } catch (error) { 
        mapDebugger.log('ERROR', 'bounds_calculation_failed', { error: error.message }); 
      }
    }
  }, [map, assets, sensors, bounds, onBoundsCalculated]);
  return null;
});
MapBoundsController.displayName = 'MapBoundsController';

// ============================================================================
// INTERACTION CONTROLLER (Drawing, Placement & Undo)
// ============================================================================
const MapInteractionController = ({ 
  isDrawingPipeline, 
  isDrawingRoad,
  isPlacingSensor, 
  draftPoints, 
  setDraftPoints, 
  onMapClickForPlacement, 
  onPipelineComplete,
  onRoadMappingComplete,
  isInspectingClearance = false
}) => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    if (isDrawingPipeline || isPlacingSensor || isDrawingRoad) {
      container.style.cursor = 'crosshair';
      if (isDrawingPipeline || isDrawingRoad) map.doubleClickZoom.disable(); 
    } else {
      container.style.cursor = '';
      map.doubleClickZoom.enable();
    }
  }, [map, isDrawingPipeline, isPlacingSensor, isDrawingRoad]);

  useEffect(() => {
    const handleClick = (e) => {
      if (isInspectingClearance) return;
      const { lat, lng } = e.latlng;
      if (isPlacingSensor && onMapClickForPlacement) {
        onMapClickForPlacement({ lat, lng });
      } else if (isDrawingPipeline || isDrawingRoad) {
        setDraftPoints(prev => [...prev, { lat, lng }]);
      }
    };

    const handleDblClick = (e) => {
      if (isInspectingClearance) return;
      if (isDrawingPipeline || isDrawingRoad) {
        L.DomEvent.stopPropagation(e);
        const { lat, lng } = e.latlng;
        const finalPoints = [...draftPoints, { lat, lng }];

        if (finalPoints.length >= 2) {
          if (isDrawingPipeline && onPipelineComplete) onPipelineComplete(finalPoints);
          if (isDrawingRoad && onRoadMappingComplete) onRoadMappingComplete(finalPoints);
        }
        setDraftPoints([]); 
      }
    };

    const handleContextMenu = (e) => {
      if (isDrawingPipeline || isDrawingRoad) {
        L.DomEvent.preventDefault(e);
        setDraftPoints(prev => (prev.length > 0 ? prev.slice(0, -1) : []));
      }
    };

    map.on('click', handleClick);
    map.on('dblclick', handleDblClick);
    map.on('contextmenu', handleContextMenu); 

    return () => {
      map.off('click', handleClick);
      map.off('dblclick', handleDblClick);
      map.off('contextmenu', handleContextMenu);
    };
  }, [
    map, 
    isDrawingPipeline, 
    isDrawingRoad, 
    isPlacingSensor, 
    draftPoints, 
    setDraftPoints, 
    onMapClickForPlacement, 
    onPipelineComplete, 
    onRoadMappingComplete, 
    isInspectingClearance
  ]);

  return null;
};

// ============================================================================
// ROAD COLOR CALCULATION (Respects AI Heatmap Overrides)
// ============================================================================
const getRoadColor = (conditionRating, status, metadata = {}) => {
  // 1. Prioritize dynamic AI Heatmap and specific stroke colors
  if (metadata?.strokeColor) return metadata.strokeColor;
  if (metadata?.color) return metadata.color;

  // 2. Status overrides
  if (status === 'maintenance_required' || status === 'closed') return '#E53E3E';
  if (status === 'under_maintenance') return '#D69E2E';
  
  // 3. Condition ratings
  switch (String(conditionRating).toLowerCase()) {
    case 'excellent': return '#38A169';
    case 'good': return '#319795';
    case 'fair': return '#D69E2E';
    case 'poor': return '#DD6B20';
    case 'critical': return '#E53E3E';
    default: return '#718096';
  }
};

// ============================================================================
// 4D SUBSURFACE CLEARANCE INSPECTOR
// ============================================================================
const CLEARANCE_SEVERITY_COLORS = Object.freeze({
  NONE: '#00d4aa',
  LOW: '#38A169',
  MEDIUM: '#D69E2E',
  HIGH: '#DD6B20',
  CRITICAL: '#ef4444',
});

const CLEARANCE_SCOPE_KEY = 'gis-map-clearance-inspector';
const DEPTH_PRESETS = [0.6, 1.0, 1.5, 2.5, 4.0];

const ClearanceInspectionOverlay = ({
  isInspecting,
  onInspectionComplete,
  isDrawingActive = false,
  defaultDepthM = 1.5,
  workOrderId = null,
}) => {
  const map = useMap();

  const isMountedRef = useRef(true);
  const requestSeqRef = useRef(0);
  const depthRef = useRef(defaultDepthM);

  const [inspectionResult, setInspectionResult] = useState(null);
  const [probePoint, setProbePoint] = useState(null);
  const [loading, setLoading] = useState(false);
  const [depthM, setDepthM] = useState(defaultDepthM);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      spatialService.abortScope(CLEARANCE_SCOPE_KEY);
    };
  }, []);

  useEffect(() => { depthRef.current = depthM; }, [depthM]);

  useEffect(() => {
    if (!isInspecting) {
      spatialService.abortScope(CLEARANCE_SCOPE_KEY);
      setInspectionResult(null);
      setProbePoint(null);
      setLoading(false);
    }
  }, [isInspecting]);

  useEffect(() => {
    if (!map) return undefined;
    const container = map.getContainer();
    if (isInspecting && !isDrawingActive) {
      container.style.cursor = 'crosshair';
    }
    return () => {
      if (isInspecting && !isDrawingActive) container.style.cursor = '';
    };
  }, [map, isInspecting, isDrawingActive]);

  const runScan = useCallback(async (lat, lng) => {
    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;

    setProbePoint({ lat, lng });
    setInspectionResult(null);
    setLoading(true);

    mapDebugger.trackEvent('clearance_scan');
    mapDebugger.log('INFO', 'clearance_scan_started', { lat, lng, depthM: depthRef.current });

    const scanResult = await spatialService.evaluateSiteClearance(
      {
        workOrderId,
        latitude: lat,
        longitude: lng,
        plannedDate: new Date(),
        excavationDepthM: depthRef.current,
      },
      { scopeKey: CLEARANCE_SCOPE_KEY }
    );

    if (!isMountedRef.current || requestSeqRef.current !== seq) return;
    if (spatialService.isIgnorable(scanResult)) {
      mapDebugger.log('DEBUG', 'clearance_scan_superseded', { lat, lng });
      return;
    }

    setLoading(false);
    setInspectionResult({ lat, lng, data: scanResult });

    mapDebugger.log(
      scanResult.status === CLEARANCE_STATUS.CLEAR ? 'SUCCESS' : 'WARNING',
      'clearance_scan_complete',
      {
        lat, lng,
        status: scanResult.status,
        severity: scanResult.severity,
        evaluationId: scanResult.evaluationId,
        utilityHits: scanResult.utilityCollisions?.length ?? 0,
        permitHits: scanResult.temporalConflicts?.length ?? 0,
      }
    );

    if (onInspectionComplete) onInspectionComplete(scanResult, { lat, lng });
  }, [workOrderId, onInspectionComplete]);

  useEffect(() => {
    if (!isInspecting || isDrawingActive || !map) return undefined;

    const handleClick = (e) => {
      const { lat, lng } = e.latlng;
      runScan(lat, lng);
    };

    map.on('click', handleClick);
    return () => map.off('click', handleClick);
  }, [map, isInspecting, isDrawingActive, runScan]);

  useEffect(() => {
    if (!isInspecting) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setInspectionResult(null);
        setProbePoint(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isInspecting]);

  const panelRefCallback = useCallback((node) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);

  const handleClose = useCallback(() => {
    setInspectionResult(null);
    setProbePoint(null);
  }, []);

  const handleRetry = useCallback(() => {
    if (probePoint) runScan(probePoint.lat, probePoint.lng);
  }, [probePoint, runScan]);

  const handleDepthChange = useCallback((value) => {
    setDepthM(value);
  }, []);

  const resultData = inspectionResult?.data ?? null;

  const banner = useMemo(() => {
    if (!resultData) return null;

    const verifiedClear = spatialService.isVerifiedClear(resultData);
    if (verifiedClear) {
      return { label: 'CLEAR', detail: resultData.severity, color: '#00d4aa', bg: 'rgba(0,212,170,0.1)', border: 'rgba(0,212,170,0.3)' };
    }
    if (resultData.status === CLEARANCE_STATUS.CLEAR) {
      return { label: 'CLEAR — UNVERIFIED', detail: 'stale service', color: '#D69E2E', bg: 'rgba(214,158,46,0.12)', border: 'rgba(214,158,46,0.4)' };
    }
    if (resultData.status === CLEARANCE_STATUS.INDETERMINATE) {
      return { label: 'BLOCKED — SCAN INCOMPLETE', detail: resultData.errorKind ?? '', color: '#ef4444', bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.4)' };
    }
    const color = CLEARANCE_SEVERITY_COLORS[resultData.severity] ?? '#ef4444';
    return { label: 'CONFLICT', detail: resultData.severity, color, bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.4)' };
  }, [resultData]);

  const haloColor = banner?.color ?? '#00d4aa';
  const scanRadiusM = resultData?.scanRadiusM ?? 15;

  if (!isInspecting && !inspectionResult) return null;

  return (
    <>
      {probePoint && (
        <>
          <Circle
            center={[probePoint.lat, probePoint.lng]}
            radius={scanRadiusM}
            pathOptions={{ color: haloColor, weight: 1, fillColor: haloColor, fillOpacity: 0.08, dashArray: '4, 6' }}
          />
          <CircleMarker
            center={[probePoint.lat, probePoint.lng]}
            radius={6}
            pathOptions={{ color: '#ffffff', weight: 2, fillColor: haloColor, fillOpacity: 1 }}
          />
        </>
      )}

      <div
        ref={panelRefCallback}
        style={{
          position: 'absolute', bottom: 20, left: 20, zIndex: 2000,
          background: '#0a1020', border: `1px solid ${banner ? banner.border : 'rgba(0,212,170,0.3)'}`,
          padding: '16px', borderRadius: '8px', color: '#e2e8f0', width: '320px',
          maxHeight: '60vh', overflowY: 'auto',
          boxShadow: '0 4px 24px rgba(0,0,0,0.8)', fontFamily: "'JetBrains Mono', monospace", fontSize: '11px'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <strong style={{ color: '#00d4aa' }}>🛡️ 4D SUBSURFACE INSPECTOR</strong>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '10px', flexWrap: 'wrap' }}>
          <span style={{ color: '#64748b', fontSize: '10px', marginRight: '2px' }}>DIG DEPTH</span>
          {DEPTH_PRESETS.map(d => (
            <button
              key={`depth-${d}`}
              onClick={() => handleDepthChange(d)}
              style={{
                padding: '2px 6px', fontSize: '10px', cursor: 'pointer', borderRadius: '3px',
                background: depthM === d ? 'rgba(0,212,170,0.15)' : 'transparent',
                border: `1px solid ${depthM === d ? 'rgba(0,212,170,0.5)' : 'rgba(100,116,139,0.3)'}`,
                color: depthM === d ? '#00d4aa' : '#94a3b8',
                fontFamily: 'inherit'
              }}
            >
              {d}m
            </button>
          ))}
        </div>

        {isInspecting && !probePoint && !loading && (
          <div style={{ color: '#64748b', lineHeight: 1.5 }}>
            Click any point on the map to scan for buried utilities and active excavation permits.
          </div>
        )}

        {loading && <div>Scanning 3D utilities &amp; permits...</div>}

        {inspectionResult && !loading && resultData && banner && (
          <div>
            <div style={{ marginBottom: '6px', fontSize: '10px', color: '#64748b' }}>
              Lat: {inspectionResult.lat.toFixed(4)}, Lng: {inspectionResult.lng.toFixed(4)} · {depthM}m
            </div>

            <div style={{ 
              padding: '6px 10px', borderRadius: '4px', fontWeight: 'bold', marginBottom: '8px',
              background: banner.bg,
              color: banner.color,
              border: `1px solid ${banner.border}`
            }}>
              STATUS: {banner.label}{banner.detail ? ` (${banner.detail})` : ''}
            </div>

            {resultData.error && (
              <div style={{ marginBottom: '8px', color: '#fca5a5', lineHeight: 1.5 }}>
                {resultData.error}
                {resultData.status === CLEARANCE_STATUS.INDETERMINATE && (
                  <button
                    onClick={handleRetry}
                    style={{ display: 'block', marginTop: '6px', padding: '4px 8px', fontSize: '10px', cursor: 'pointer', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', color: '#ef4444', borderRadius: '3px', fontFamily: 'inherit' }}
                  >
                    ↻ Retry scan
                  </button>
                )}
              </div>
            )}

            {Array.isArray(resultData.warnings) && resultData.warnings.map((w, i) => (
              <div key={`warn-${i}`} style={{ marginBottom: '6px', color: '#D69E2E', fontSize: '10px', lineHeight: 1.4 }}>
                ⚠ {w}
              </div>
            ))}

            {resultData.utilityCollisions?.length > 0 && (
              <div style={{ marginBottom: '6px' }}>
                <span style={{ color: '#fb923c', fontWeight: 'bold' }}>⚠️ Nearby Utilities:</span>
                <ul style={{ paddingLeft: '16px', margin: '2px 0' }}>
                  {resultData.utilityCollisions.map((u, i) => (
                    <li key={u.id ? `${u.source}-${u.id}` : `util-${i}`} style={{ marginBottom: '3px' }}>
                      <span style={{ color: CLEARANCE_SEVERITY_COLORS[u.severity] ?? '#e2e8f0' }}>
                        {u.utility_name || u.material}
                      </span>
                      {' '}(~{u.distance_m}m away)
                      {u.requires_hand_excavation && (
                        <span style={{ color: '#ef4444', fontWeight: 'bold' }}> — HAND DIG</span>
                      )}
                      {u.depth_unknown && (
                        <span style={{ color: '#D69E2E' }}> — depth unsurveyed</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {resultData.temporalConflicts?.length > 0 && (
              <div>
                <span style={{ color: '#ef4444', fontWeight: 'bold' }}>🛑 Permit / Moratorium Overlap:</span>
                <ul style={{ paddingLeft: '16px', margin: '2px 0' }}>
                  {resultData.temporalConflicts.map((p, i) => (
                    <li key={p.id ? `permit-${p.id}` : `permit-${i}`} style={{ marginBottom: '3px' }}>
                      {p.agency_name}
                      {p.conflict_type === 'PAVING_MORATORIUM' ? ' (paving moratorium)' : ' (active permit)'}
                      {p.target_start_date && (
                        <span style={{ color: '#64748b' }}> · {p.target_start_date}{p.target_end_date ? ` → ${p.target_end_date}` : ''}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {resultData.evaluationId && (
              <div style={{ marginTop: '10px', paddingTop: '6px', borderTop: '1px solid rgba(100,116,139,0.2)', fontSize: '9px', color: '#475569' }}>
                Audit ref: {resultData.evaluationId}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

// ============================================================================
// MAIN GIS MAP COMPONENT
// ============================================================================
const GisMapView = ({
  issueLocations = [], 
  fieldZones = [], 
  pipelines = [],
  assets = [],
  sensors = [],
  readings = {},             
  sensorPipelineLinks = {},  
  criticalWellIds = new Set(),
  selectedEntity = { type: null, id: null },
  selectedAsset = null,
  selectedSensor = null,
  selectedPipeline = null,
  onAssetSelect,
  anomalies = [],
  onSensorSelect,
  onPipelineClick,           
  onZoneClick,
  onMapClickForPlacement,    
  isDrawingPipeline = false, 
  isDrawingRoad = false,
  isPlacingSensor = false,   
  isLinkingMode = false,     
  onPipelineComplete,        
  onRoadMappingComplete,
  showAlerts = true,
  mapCenter = [25.047, -77.355], 
  mapZoom = 13,
  onMapReady,
  isInspectingClearance = false,
  onClearanceInspectionComplete,
  clearanceWorkOrderId = null,
  clearanceDefaultDepthM = 1.5,
  // --- UPDATE (GIS map — road segment health layer)
  roadSegments = null,
  roadDisplayMode = 'roads',
  segmentBands = null,
  showSegmentLabels = false,
  selectedSegmentId = null,
  onSegmentSelect,
  onOpenRoadReport,
  showAssetMarkers = true,
  showSensorMarkers = true,
  showAnomalies = true,
  // --- END UPDATE
  // --- UPDATE (roads v1.4)
  pondingForecast = null,
  showPonding = false,
  pondingHour = null,
  selectedPondingSegmentId = null,
  onPondingSegmentSelect,
  onPondingOpenReport,
  // --- END UPDATE
  // --- UPDATE (Public Utilities v3)
  puLayer = null,
  puColorBy = 'type',
  puVisibleTypes = null,
  showPuZones = true,
  selectedPuId = null,
  onPuFeatureSelect,
  puFitOnLoad = true,
  showPuLegend = true,
  liveConnected,
  // --- END UPDATE
}) => {
  mapDebugger.incrementRender();

  const { isConnected: ctxConnected } = useNotifications();
  const isConnected = liveConnected ?? ctxConnected; // --- UPDATE (v3): the page's socket state wins when given
  // --- UPDATE (v3): PU legend (real counts from the server)
  const puLegendRows = useMemo(() => (puLayer ? puLegend(puLayer, puColorBy) : []), [puLayer, puColorBy]);
  const [puLegendOpen, setPuLegendOpen] = useState(() => (typeof window === 'undefined' ? true : window.innerWidth >= 640));
  // --- END UPDATE

  const [, setMapInstance] = useState(null);
  const [bounds, setBounds] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [, setTileLayerLoaded] = useState(false);
  
  const [draftPoints, setDraftPoints] = useState([]); 

  const mapRef = useRef(null);
  const alertPollingRef = useRef(null);

  useEffect(() => {
    if (!isDrawingPipeline && !isDrawingRoad) setDraftPoints([]);
  }, [isDrawingPipeline, isDrawingRoad]);

  // --- UPDATE (GIS map — road segment health layer)
  const segmentsMode = roadDisplayMode === 'segments' && !!roadSegments && !isDrawingRoad;
  const segmentRoadIds = useMemo(
    () => new Set(segmentsMode ? (roadSegments?.roads || []).map(r => String(r.id)) : []),
    [segmentsMode, roadSegments]
  );
  const segmentSummary = useMemo(
    () => (segmentsMode ? summarize(filterSegments(roadSegments?.segments || [], null)) : null),
    [segmentsMode, roadSegments]
  );
  // legend starts collapsed on phones so it doesn't cover the map
  const [legendOpen, setLegendOpen] = useState(() => (typeof window === 'undefined' ? true : window.innerWidth >= 640));
  // --- END UPDATE

  const isDrawingActive = useMemo(
    () => isDrawingPipeline || isDrawingRoad || isPlacingSensor,
    [isDrawingPipeline, isDrawingRoad, isPlacingSensor]
  );

  // Auto-pan and zoom on entity selection
  useEffect(() => {
    if (!mapRef.current || !selectedEntity?.id) return;

    if (selectedEntity.type === 'sensor') {
      const s = sensors.find(item => String(item.device_id || item.id) === String(selectedEntity.id));
      if (s?.latitude && s?.longitude) {
        mapRef.current.setView([parseFloat(s.latitude), parseFloat(s.longitude)], 17, { animate: true });
      }
    } else if (selectedEntity.type === 'asset' || selectedEntity.type === 'well' || selectedEntity.type === 'road') {
      const a = assets.find(item => String(item.id) === String(selectedEntity.id));
      if (a?.latitude && a?.longitude) {
        mapRef.current.setView([parseFloat(a.latitude), parseFloat(a.longitude)], 17, { animate: true });
      } else if (a?.geo_point?.coordinates) {
        const coords = a.geo_point.coordinates;
        if (a.geo_point.type === 'Point') {
          mapRef.current.setView([coords[1], coords[0]], 17, { animate: true });
        } else if (a.geo_point.type === 'LineString' && coords[0]) {
          mapRef.current.setView([coords[0][1], coords[0][0]], 16, { animate: true });
        }
      }
    } else if (selectedEntity.type === 'pipeline') {
      const p = pipelines.find(item => String(item.id) === String(selectedEntity.id));
      let coords = p?.path_coordinates;
      if (typeof coords === 'string') {
        try { coords = JSON.parse(coords); } catch (e) { coords = []; }
      }
      if (Array.isArray(coords) && coords.length > 0 && coords[0]?.lat && coords[0]?.lng) {
        mapRef.current.setView([coords[0].lat, coords[0].lng], 16, { animate: true });
      }
    }
  }, [selectedEntity, sensors, assets, pipelines]);

  const getDynamicPipelineColor = useCallback((pipeline) => {
    const linkedSensorsForPipeline = sensors.filter(s => {
      const sensorId = s.device_id || s.id || s.name;
      const linkId = sensorPipelineLinks[sensorId] || s.pipeline_id || s.linked_pipeline;
      return String(linkId) === String(pipeline.id);
    });

    if (linkedSensorsForPipeline.length > 0) {
      const hasActiveAlert = alerts.some(alert => 
        linkedSensorsForPipeline.some(s => (s.device_id || s.id) === alert.sensor_id) &&
        alert.status !== 'resolved'
      );

      if (hasActiveAlert && isConnected) return '#F44336'; 

      let isHigh = false;
      let isLow = false;

      linkedSensorsForPipeline.forEach(sensor => {
        const sensorId = sensor.device_id || sensor.id;
        const liveData = readings[sensorId];

        if (liveData && !isConnected) return;

        const currentPsi = liveData ? (liveData.psi ?? liveData.pressure) : (sensor.psi ?? sensor.lastValue);

        if (currentPsi !== undefined && !isNaN(parseFloat(currentPsi))) {
          const val = parseFloat(currentPsi);
          if (val > 100) isHigh = true;
          else if (val < 20) isLow = true;
        }
      });

      if (isHigh) return '#E53E3E'; 
      if (isLow) return '#DD6B20';  
      return '#38A169'; 
    }

    const fallbackColors = { 'AREA1': '#3182CE', 'AREA2': '#805AD5', 'AREA3': '#00B5D8', 'default': '#A0A0A0' };
    return fallbackColors[pipeline.area_code] || fallbackColors['default'];
  }, [sensors, sensorPipelineLinks, readings, alerts, isConnected]); 

  const alertsByAsset = useMemo(() => alerts.reduce((acc, alert) => {
    if (!acc[alert.assetId]) acc[alert.assetId] = [];
    acc[alert.assetId].push(alert);
    return acc;
  }, {}), [alerts]);

  const validMapCenter = useMemo(() => {
    if (Array.isArray(mapCenter) && mapCenter.length === 2 && !isNaN(mapCenter[0]) && !isNaN(mapCenter[1])) return mapCenter;
    return [25.047, -77.355];
  }, [mapCenter]);

  const handleMapReady = useCallback((map) => {
    setMapInstance(map);
    mapRef.current = map;
    setMapReady(true);
    if (onMapReady) onMapReady(map);
    if (!map) { mapRef.current = null; setMapReady(false); } // --- UPDATE (roads v1.4): unmounted
  }, [onMapReady]);

  const handleAssetClick = useCallback((asset) => {
    if (onAssetSelect) onAssetSelect(asset);
    
    if (mapRef.current) {
      if (asset.latitude && asset.longitude) {
        mapRef.current.setView([parseFloat(asset.latitude), parseFloat(asset.longitude)], 16, { animate: true });
      } else if (asset.geo_point && asset.geo_point.coordinates) {
        if (asset.geo_point.type === 'Point') {
          mapRef.current.setView([asset.geo_point.coordinates[1], asset.geo_point.coordinates[0]], 16, { animate: true });
        } else if (asset.geo_point.type === 'LineString') {
          mapRef.current.setView([asset.geo_point.coordinates[0][1], asset.geo_point.coordinates[0][0]], 16, { animate: true });
        }
      }
    }
  }, [onAssetSelect]);

  const handleSensorClick = useCallback((sensor) => {
    if (onSensorSelect) onSensorSelect(sensor);
    if (mapRef.current && sensor.latitude && sensor.longitude) {
      mapRef.current.setView([parseFloat(sensor.latitude), parseFloat(sensor.longitude)], 16, { animate: true });
    }
  }, [onSensorSelect]);

  const fetchAlerts = useCallback(async () => {
    if (!showAlerts || !isConnected) return;
    try {
      setLoading(true);
      const response = await predictiveMaintenanceService.fetchAlerts({ isResolved: false });
      setAlerts(response.data || []);
      setError(null);
    } catch (err) { 
      setError('Failed to load alerts'); 
      setAlerts([]); 
    } finally { 
      setLoading(false); 
    }
  }, [showAlerts, isConnected]);

  const refreshMap = useCallback(() => {
    if (mapRef.current) mapRef.current.invalidateSize();
    fetchAlerts();
  }, [fetchAlerts]);

  useEffect(() => {
    fetchAlerts();
    if (showAlerts) alertPollingRef.current = setInterval(fetchAlerts, 30000);
    return () => { if (alertPollingRef.current) clearInterval(alertPollingRef.current); };
  }, [showAlerts, fetchAlerts]);

  return (
    <MapErrorBoundary>
      <div className="gis-map-container" style={{ width: '100%', height: '100%', position: 'relative' }}>
        
        {loading && (
          <div className="map-loading-overlay">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading alerts...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="map-error-banner" style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, background: '#fff', padding: '10px', borderRadius: '5px', boxShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>
            <i className="fas fa-exclamation-triangle"></i> {error}
            <button onClick={refreshMap} className="btn btn-sm btn-link">Retry</button>
          </div>
        )}

        {!isConnected && (
          <div className="map-error-banner" style={{ position: 'absolute', top: error ? 60 : 10, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, background: '#FFF3CD', color: '#DD6B20', padding: '10px 15px', borderRadius: '5px', boxShadow: '0 2px 10px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' }}>
            <i className="fas fa-wifi"></i> Live Feed Disconnected — Showing Stale Data
          </div>
        )}

        <MapContainer
          center={validMapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%', borderRadius: 'inherit' }}
          zoomControl={true}
          attributionControl={true}
        >
          <TileLayer
            url="https://basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}.png?key=cb1_4avm_1_5bd6e706e63138d449b32f3e"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            eventHandlers={{ load: () => setTileLayerLoaded(true), tileerror: () => setError('Failed to load tiles') }}
          />

          <MapInitializer onMapReady={handleMapReady} />
          <MapResizeController />
          
          <MapInteractionController 
            isDrawingPipeline={isDrawingPipeline}
            isDrawingRoad={isDrawingRoad}
            isPlacingSensor={isPlacingSensor}
            draftPoints={draftPoints}
            setDraftPoints={setDraftPoints}
            onMapClickForPlacement={onMapClickForPlacement}
            onPipelineComplete={onPipelineComplete}
            onRoadMappingComplete={onRoadMappingComplete}
            isInspectingClearance={isInspectingClearance}
          />

          {mapReady && (
            <ClearanceInspectionOverlay
              isInspecting={isInspectingClearance}
              onInspectionComplete={onClearanceInspectionComplete}
              isDrawingActive={isDrawingActive}
              workOrderId={clearanceWorkOrderId}
              defaultDepthM={clearanceDefaultDepthM}
            />
          )}

          {mapReady && (
            <>
              <MapBoundsController assets={assets} sensors={sensors} bounds={bounds} onBoundsCalculated={setBounds} />
              
              {/* Field Operation Zones */}
              {fieldZones && fieldZones.map(zone => {
                if (!zone.geojson) return null;
                
                const hexColors = { red: '#E53E3E', orange: '#DD6B20', yellow: '#D69E2E', green: '#38A169', blue: '#3182CE', purple: '#805AD5', teal: '#319795', cyan: '#00B5D8' };
                const zoneColor = hexColors[zone.color] || '#3182CE';

                return (
                  <GeoJSON
                    key={`zone-${zone.id}-${zone.count}`}
                    data={zone.geojson}
                    style={{
                      color: zoneColor,
                      weight: 2,
                      fillColor: zoneColor,
                      fillOpacity: 0.15,
                      dashArray: '5, 5'
                    }}
                    eventHandlers={{
                      click: () => onZoneClick && onZoneClick(zone)
                    }}
                  >
                    <LeafletTooltip sticky direction="top" opacity={0.95}>
                      <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '2px' }}>{zone.area}</div>
                      <div style={{ fontSize: '12px', color: '#4A5568' }}>{zone.count} Active Issues</div>
                      <div style={{ fontSize: '10px', color: '#718096', marginTop: '4px' }}>Click to view zone details</div>
                    </LeafletTooltip>
                  </GeoJSON>
                );
              })}

              {/* Roads & Infrastructure with AI Heatmap Color Hook */}
              {assets && assets.filter(a => a.type === 'road' || a.type === 'street').map(road => {
                if (!road.geo_point && !road.path_coordinates && !road.polyline_points) return null;
                // --- UPDATE (segment layer): its segments (or the dashed "not sliced" line) replace the road line
                if (segmentsMode && segmentRoadIds.has(String(road.id))) return null;
                // --- END UPDATE

                const conditionRating = road.metadata?.condition_rating || 'unrated';
                const roadColor = getRoadColor(conditionRating, road.status, road.metadata);
                const isSelected = (selectedEntity?.type === 'asset' && String(selectedEntity.id) === String(road.id)) ||
                                   (selectedAsset && selectedAsset.id === road.id);
                const weight = isSelected ? 8 : (road.metadata?.strokeColor ? 6 : 4);
                const opacity = isSelected ? 1 : (road.metadata?.strokeColor ? 0.95 : 0.75);

                const tooltipContent = (
                  <LeafletTooltip sticky direction="top" opacity={0.95}>
                    <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '2px' }}>{road.name}</div>
                    <div style={{ fontSize: '12px', color: '#4A5568' }}>Code: {road.metadata?.road_code || 'N/A'}</div>
                    <div style={{ fontSize: '12px', color: '#4A5568' }}>
                      Condition: <span style={{ color: roadColor, fontWeight: 'bold', textTransform: 'capitalize' }}>{conditionRating}</span>
                    </div>
                    {road.ai_rul?.predicted_rul_years != null && (
                      <div style={{ fontSize: '12px', color: '#805AD5', fontWeight: 'bold' }}>
                        AI RUL: {road.ai_rul.predicted_rul_years} yrs
                      </div>
                    )}
                  </LeafletTooltip>
                );

                let polyData = road.polyline_points || road.path_coordinates || road.geo_point;
                if (typeof polyData === 'string') {
                  try { polyData = JSON.parse(polyData); } catch (e) { polyData = null; }
                }

                if (Array.isArray(polyData)) {
                  const positions = polyData
                    .filter(p => p && typeof p.lat === 'number' && typeof p.lng === 'number')
                    .map(p => [p.lat, p.lng]);
                  
                  if (positions.length >= 2) {
                    return (
                      <Polyline
                        key={`road-poly-${road.id}`}
                        positions={positions}
                        pathOptions={{ color: roadColor, weight, opacity }}
                        eventHandlers={{ click: () => handleAssetClick(road) }}
                      >
                        {tooltipContent}
                      </Polyline>
                    );
                  }
                }

                if (road.geo_point) {
                  let geoData = road.geo_point;
                  if (typeof geoData === 'string') {
                    try { geoData = JSON.parse(geoData); } catch (e) { return null; }
                  }

                  if (geoData && geoData.type) {
                    return (
                      <GeoJSON
                        key={`road-geo-${road.id}`}
                        data={geoData}
                        style={{ color: roadColor, weight, opacity }}
                        eventHandlers={{ click: () => handleAssetClick(road) }}
                      >
                        {tooltipContent}
                      </GeoJSON>
                    );
                  }
                }

                return null;
              })}

              {/* --- UPDATE (Public Utilities v3): PU-only asset layer (canvas + clusters + constituencies) */}
              {puLayer && (
                <PuAssetLayer
                  features={puLayer.features || []}
                  zones={puLayer.zones || []}
                  managers={puLayer.managers || []}
                  colorBy={puColorBy}
                  visibleTypes={puVisibleTypes}
                  showZones={showPuZones}
                  selectedId={selectedPuId}
                  onSelect={onPuFeatureSelect}
                  fitOnLoad={puFitOnLoad}
                />
              )}
              {/* --- END UPDATE */}

              {/* --- UPDATE (GIS map — road segment health layer) */}
              {segmentsMode && (
                <RoadSegmentLayer
                  data={roadSegments}
                  visibleBands={segmentBands}
                  showLabels={showSegmentLabels}
                  selectedSegmentId={selectedSegmentId}
                  onSegmentSelect={onSegmentSelect}
                  onOpenRoadReport={onOpenRoadReport}
                />
              )}
              {/* --- END UPDATE */}
              {/* --- UPDATE (roads v1.4): road ponding forecast (drawn over the road / segment lines) */}
              {pondingForecast && (
                <PondingLayerBridge
                  forecast={pondingForecast}
                  hour={pondingHour}
                  visible={showPonding && !isDrawingRoad}
                  selectedSegmentId={selectedPondingSegmentId}
                  onSelectSegment={onPondingSegmentSelect}
                  onOpenReport={onPondingOpenReport}
                />
              )}
              {/* --- END UPDATE */}

              {/* AI Cluster Spiderweb Tethers */}
              {issueLocations && issueLocations.map(issue => {
                if (issue.merged_into_issue_id) {
                  const parent = issueLocations.find(p => p.id === issue.merged_into_issue_id);
                  if (parent && parent.lat && parent.lng && issue.lat && issue.lng) {
                    return (
                      <Polyline
                        key={`tether-cluster-${issue.id}-${parent.id}`}
                        positions={[
                          [issue.lat, issue.lng],
                          [parent.lat, parent.lng]
                        ]}
                        pathOptions={{
                          color: "#A0AEC0",
                          weight: 2,
                          dashArray: '4, 6',
                          opacity: 0.8
                        }}
                      />
                    );
                  }
                }
                return null;
              })}

              {/* Pipeline Polyline Renderer */}
              {pipelines.map(pipeline => {
                let pathCoords = pipeline.path_coordinates;
                if (typeof pathCoords === 'string') {
                  try { pathCoords = JSON.parse(pathCoords); } catch (e) { return null; }
                }
                if (!Array.isArray(pathCoords) || pathCoords.length < 2) return null;

                const positions = pathCoords
                  .filter(p => p && typeof p.lat === 'number' && typeof p.lng === 'number')
                  .map(p => [p.lat, p.lng]);

                if (positions.length < 2) return null;

                const isSelected = (selectedEntity?.type === 'pipeline' && String(selectedEntity.id) === String(pipeline.id)) ||
                                   (selectedPipeline && String(selectedPipeline.id) === String(pipeline.id));
                const dynamicColor = isSelected ? '#38B2AC' : getDynamicPipelineColor(pipeline);
                const dynamicWeight = isSelected ? 9 : (dynamicColor === '#E53E3E' || dynamicColor === '#DD6B20' ? 7 : 5);
                const dynamicOpacity = isSelected ? 1.0 : 0.8;

                return (
                  <Polyline
                    key={`pipeline-${pipeline.id}`}
                    positions={positions}
                    pathOptions={{
                      color: dynamicColor,
                      weight: dynamicWeight,
                      opacity: dynamicOpacity,
                      dashArray: isSelected ? '8, 8' : undefined
                    }}
                    eventHandlers={{
                      click: () => onPipelineClick && onPipelineClick(pipeline)
                    }}
                  />
                );
              })}

              {/* Draft drawing overlay for pipelines and roads */}
              {(isDrawingPipeline || isDrawingRoad) && draftPoints.length > 0 && (
                <>
                  <Polyline 
                    positions={draftPoints.map(p => [p.lat, p.lng])} 
                    pathOptions={{ 
                      color: isDrawingRoad ? '#3182CE' : '#9F7AEA',
                      weight: 5, 
                      dashArray: '10, 10' 
                    }} 
                  />
                  {draftPoints.map((pt, idx) => (
                    <CircleMarker 
                      key={`draft-pt-${idx}`} 
                      center={[pt.lat, pt.lng]} 
                      radius={5} 
                      pathOptions={{ 
                        fillColor: isDrawingRoad ? '#3182CE' : '#9F7AEA', 
                        color: '#ffffff', 
                        weight: 2, 
                        fillOpacity: 1 
                      }} 
                    />
                  ))}
                </>
              )}

              {/* Sensor Association Tethers */}
              {sensors.map(sensor => {
                if (!sensor.latitude || !sensor.longitude) return null;

                const isLinkedToAsset = sensor.linked_entity_type && sensor.linked_entity_type !== 'none' && sensor.linked_entity_type !== 'pipeline';
                const isLinkedToPipeline = sensor.linked_entity_type === 'pipeline' || sensorPipelineLinks[sensor.device_id || sensor.id];

                if (isLinkedToAsset && sensor.linked_entity_id) {
                  const linkedAsset = assets.find(a => String(a.id) === String(sensor.linked_entity_id));
                  if (linkedAsset && linkedAsset.latitude && linkedAsset.longitude) {
                    return (
                      <Polyline
                        key={`tether-asset-${sensor.device_id || sensor.id}`}
                        positions={[
                          [parseFloat(sensor.latitude), parseFloat(sensor.longitude)],
                          [parseFloat(linkedAsset.latitude), parseFloat(linkedAsset.longitude)]
                        ]}
                        pathOptions={{
                          color: alerts.some(a => a.sensor_id === (sensor.device_id || sensor.id) && a.status !== 'resolved') 
                            ? '#F44336' 
                            : '#A0AEC0', 
                          weight: 3,
                          dashArray: '5, 5',
                          opacity: 0.8
                        }}
                      />
                    );
                  }
                }

                if (isLinkedToPipeline) {
                  const pipelineId = sensor.linked_entity_id || sensorPipelineLinks[sensor.device_id || sensor.id];
                  const linkedPipeline = pipelines.find(p => String(p.id) === String(pipelineId));
                  
                  if (linkedPipeline && linkedPipeline.path_coordinates) {
                    let coords = linkedPipeline.path_coordinates;
                    if (typeof coords === 'string') {
                      try { coords = JSON.parse(coords); } catch (e) { coords = []; }
                    }
                    
                    if (Array.isArray(coords) && coords.length > 0 && coords[0].lat && coords[0].lng) {
                      return (
                        <Polyline
                          key={`tether-pipe-${sensor.device_id || sensor.id}`}
                          positions={[
                            [parseFloat(sensor.latitude), parseFloat(sensor.longitude)],
                            [coords[0].lat, coords[0].lng] 
                          ]}
                          pathOptions={{
                            color: '#A0AEC0',
                            weight: 2,
                            dashArray: '5, 5',
                            opacity: 0.6
                          }}
                        />
                      );
                    }
                  }
                }
                return null;
              })}

              {/* Edge Anomaly Visualizer & Feedback Engine */}
              {showAnomalies && anomalies && anomalies.map((anomaly) => { /* --- UPDATE: showAnomalies toggle */
                const confPercent = anomaly.confidence != null 
                  ? (parseFloat(anomaly.confidence) * 100).toFixed(1) 
                  : '0.0';

                return (
                  <CircleMarker
                    key={`anomaly-${anomaly.id}`}
                    center={[parseFloat(anomaly.latitude), parseFloat(anomaly.longitude)]}
                    radius={6}
                    pathOptions={{ 
                      color: '#E53E3E',
                      fillColor: '#FC8181',
                      fillOpacity: 0.9, 
                      weight: 2 
                    }}
                  >
                    <Popup maxWidth={260}>
                      <div style={{ padding: '4px', textAlign: 'center' }}>
                        <strong style={{ color: '#E53E3E', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                          AI Detection: {anomaly.type}
                        </strong>
                        
                        {anomaly.photo_url ? (
                          <img 
                            src={anomaly.photo_url} 
                            alt="Detected Anomaly" 
                            style={{ 
                              width: '100%', 
                              maxHeight: '140px', 
                              objectFit: 'cover', 
                              borderRadius: '6px',
                              marginBottom: '8px', 
                              border: '1px solid #E2E8F0'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div style={{ padding: '10px', background: '#EDF2F7', borderRadius: '4px', marginBottom: '8px', fontSize: '12px' }}>
                            No Image Available
                          </div>
                        )}

                        <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                          <b>Confidence:</b> {confPercent}%<br/>
                          <b>Status:</b> {String(anomaly.status || '').replace(/_/g, ' ')}<br/>
                        </div>

                        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await fetch(`/api/v1/anomalies/telemetry/${anomaly.id}/feedback`, {
                                  method: 'PATCH',
                                  headers: { 
                                    'Content-Type': 'application/json', 
                                    'Authorization': `Bearer ${localStorage.getItem('token')}` 
                                  },
                                  body: JSON.stringify({ is_true_positive: true })
                                });
                                e.target.closest('.leaflet-popup').style.display = 'none';
                              } catch (err) { 
                                console.error("Feedback confirmation failed:", err); 
                              }
                            }}
                            style={{ flex: 1, padding: '6px', background: '#38A169', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}
                          >
                            ✓ Confirm
                          </button>
                          
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await fetch(`/api/v1/anomalies/telemetry/${anomaly.id}/feedback`, {
                                  method: 'PATCH',
                                  headers: { 
                                    'Content-Type': 'application/json', 
                                    'Authorization': `Bearer ${localStorage.getItem('token')}` 
                                  },
                                  body: JSON.stringify({ is_true_positive: false })
                                });
                                e.target.closest('.leaflet-popup').style.display = 'none';
                              } catch (err) { 
                                console.error("Feedback rejection failed:", err); 
                              }
                            }}
                            style={{ flex: 1, padding: '6px', background: '#E53E3E', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}
                          >
                            ✗ False Alarm
                          </button>
                        </div>

                        <span style={{ fontSize: '10px', color: '#718096', display: 'block', marginTop: '6px' }}>
                          {new Date(anomaly.created_at).toLocaleString()}
                        </span>
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}

              {/* Dynamic Flood Inundation Buffer Zones */}
              {assets
                .filter(a => (a.type || '').toLowerCase() === 'well' && criticalWellIds.has(String(a.id)))
                .map(well => {
                  if (!well.latitude || !well.longitude) return null;
                  return (
                    <React.Fragment key={`flood-hazard-ring-${well.id}`}>
                      {/* Outer Inundation Zone (400m) */}
                      <Circle
                        center={[parseFloat(well.latitude), parseFloat(well.longitude)]}
                        radius={400}
                        pathOptions={{
                          color: '#E53E3E',
                          fillColor: '#E53E3E',
                          fillOpacity: 0.12,
                          weight: 1.5,
                          dashArray: '6, 6'
                        }}
                      />
                      {/* Immediate Core Danger Zone (150m) */}
                      <Circle
                        center={[parseFloat(well.latitude), parseFloat(well.longitude)]}
                        radius={150}
                        pathOptions={{
                          color: '#E53E3E',
                          fillColor: '#E53E3E',
                          fillOpacity: 0.28,
                          weight: 2
                        }}
                      />
                    </React.Fragment>
                  );
                })}

              {/* --- UPDATE: marker layers can be toggled off (showAssetMarkers / showSensorMarkers) */}
              {showAssetMarkers && (
              <AssetMarkers 
                assets={assets} 
                selectedAsset={selectedAsset} 
                onAssetClick={handleAssetClick} 
                alertsByAsset={alertsByAsset} 
                showAlerts={showAlerts} 
                sensors={sensors}
                readings={readings}
                isConnected={isConnected} 
                criticalWellIds={criticalWellIds}
              />
              )}
              
              {showSensorMarkers && (
              <SensorMarkers 
                sensors={sensors} 
                selectedSensor={selectedSensor} 
                onSensorClick={handleSensorClick} 
                readings={readings} 
                sensorPipelineLinks={sensorPipelineLinks}
                isConnected={isConnected} 
              />
              )}

              {showAssetMarkers && <TechnicianMarkers technicians={assets.technicians || []} />}
              {/* --- END UPDATE */}
            </>
          )}
        </MapContainer>

        {/* --- UPDATE (Public Utilities v3): PU legend — what each colour means, with real counts */}
        {puLayer && showPuLegend && !segmentsMode && !isInspectingClearance && (
          <div
            data-testid="pu-map-legend"
            style={{
              position: 'absolute', left: 12, bottom: 28, zIndex: 1000, background: 'rgba(10,16,32,0.88)', color: '#E2E8F0',
              borderRadius: 10, padding: '10px 12px', fontSize: 11, minWidth: 180, maxWidth: 260, maxHeight: '45%', overflowY: 'auto', boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
            }}
          >
            <button type="button" onClick={() => setPuLegendOpen(v => !v)} aria-expanded={puLegendOpen}
              style={{ all: 'unset', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', width: '100%', fontWeight: 800, marginBottom: puLegendOpen ? 6 : 0, fontSize: 12 }}>
              <span>{(PU_COLOR_BY.find(o => o.value === puColorBy) || PU_COLOR_BY[0]).label}</span>
              <span style={{ color: '#A0AEC0', marginLeft: 8 }}>{puLegendOpen ? '▾' : '▸'}</span>
            </button>
            {puLegendOpen && puLegendRows.map(r => (
              <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: r.color, display: 'inline-block', border: '1px solid rgba(255,255,255,.7)', flexShrink: 0 }} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>
                <b>{r.count.toLocaleString()}</b>
              </div>
            ))}
            {puLegendOpen && (
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(160,174,192,0.25)', color: '#A0AEC0' }}>
                {(puLayer.total || 0).toLocaleString()} on the map{puLayer.no_gps ? ` · ${puLayer.no_gps} without GPS` : ''}
                <div><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', border: '2px solid #E53E3E', marginRight: 6, verticalAlign: 'middle' }} />overdue, failed or damaged</div>
              </div>
            )}
          </div>
        )}
        {/* --- END UPDATE */}

        {/* --- UPDATE (roads v1.4): rain-water legend (band counts at the shown hour) */}
        {showPonding && pondingForecast && !isDrawingRoad && !isInspectingClearance && (() => {
          const segs = pondingForecast.segments || [];
          const counts = Object.fromEntries(PONDING_BANDS.map(k => [k, 0]));
          segs.forEach(s => { const b = pondingBandAt(s, pondingHour); if (counts[b] !== undefined) counts[b] += 1; });
          const t = pondingHour != null ? pondingForecast.timeline?.[pondingHour] : null;
          return (
            <div data-testid="ponding-legend" style={{ position: 'absolute', right: 12, bottom: 28, zIndex: 1000, background: 'rgba(10,16,32,0.88)', color: '#E2E8F0', borderRadius: 10, padding: '10px 12px', fontSize: 11, minWidth: 170, boxShadow: '0 4px 16px rgba(0,0,0,0.5)' }}>
              <div style={{ fontWeight: 800, fontSize: 12, marginBottom: 6 }}>💧 Rain water {pondingHour == null ? '(peak)' : `(+${pondingHour} h)`}</div>
              {PONDING_BANDS.map(k => (
                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0' }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: pondingBand(k).hex, display: 'inline-block', border: '1px solid rgba(255,255,255,.7)' }} />
                  <span style={{ flex: 1 }}>{pondingBand(k).label}</span><b>{counts[k]}</b>
                </div>
              ))}
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(160,174,192,0.25)', color: '#A0AEC0' }}>
                {t ? `${t.rain_mm} mm rain this hour` : `${pondingForecast.summary?.rain_total_mm ?? 0} mm forecast`}
                <div><span style={{ display: 'inline-block', width: 14, borderTop: '2px dashed #E53E3E', marginRight: 6, verticalAlign: 'middle' }} />drain forecast to fail</div>
              </div>
            </div>
          );
        })()}
        {/* --- END UPDATE */}

        {/* --- UPDATE (GIS map — road segment health layer): legend with counts */}
        {segmentsMode && segmentSummary && !isInspectingClearance && (
          <div
            data-testid="segment-legend"
            style={{
              position: 'absolute', left: 12, bottom: 28, zIndex: 1000, background: 'rgba(10,16,32,0.88)', color: '#E2E8F0',
              borderRadius: 10, padding: '10px 12px', fontSize: 11, minWidth: 170, boxShadow: '0 4px 16px rgba(0,0,0,0.5)', pointerEvents: 'auto'
            }}
          >
            <button
              type="button"
              onClick={() => setLegendOpen(v => !v)}
              aria-expanded={legendOpen}
              style={{ all: 'unset', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', width: '100%', fontWeight: 800, marginBottom: legendOpen ? 6 : 0, fontSize: 12 }}
            >
              <span>Segment health (PCI)</span><span style={{ color: '#A0AEC0', marginLeft: 8 }}>{legendOpen ? '▾' : '▸'}</span>
            </button>
            {legendOpen && SEGMENT_BANDS.map(b => {
              const hidden = segmentBands && !(segmentBands instanceof Set ? segmentBands.has(b.key) : segmentBands.includes(b.key));
              return (
                <div key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0', opacity: hidden ? 0.4 : 1 }}>
                  <span style={{ width: 18, height: 5, borderRadius: 3, background: b.color, display: 'inline-block', backgroundImage: b.key === 'unscored' ? 'repeating-linear-gradient(90deg, #A0AEC0 0 4px, transparent 4px 7px)' : undefined }} />
                  <span style={{ flex: 1 }}>{b.label} <span style={{ color: '#718096' }}>{b.range}</span></span>
                  <b>{segmentSummary.counts[b.key] || 0}</b>
                </div>
              );
            })}
            {legendOpen && <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(160,174,192,0.25)', color: '#A0AEC0' }}>
              {segmentSummary.total} segments · avg PCI {segmentSummary.avgPci ?? '—'}
              {(roadSegments?.unsegmented || []).length > 0 && <div>{roadSegments.unsegmented.length} road(s) not sliced (dashed)</div>}
            </div>}
          </div>
        )}
        {/* --- END UPDATE */}
      </div>
    </MapErrorBoundary>
  );
};

export default memo(GisMapView, (prevProps, nextProps) => {
  const prevCenter = prevProps.mapCenter || [];
  const nextCenter = nextProps.mapCenter || [];
  
  return (
    prevProps.criticalWellIds === nextProps.criticalWellIds &&
    prevProps.selectedEntity?.type === nextProps.selectedEntity?.type &&
    prevProps.selectedEntity?.id === nextProps.selectedEntity?.id &&
    prevProps.issueLocations === nextProps.issueLocations &&
    prevProps.fieldZones === nextProps.fieldZones && 
    prevProps.assets === nextProps.assets &&
    prevProps.sensors === nextProps.sensors &&
    prevProps.pipelines === nextProps.pipelines &&
    prevProps.anomalies === nextProps.anomalies &&
    prevProps.isDrawingPipeline === nextProps.isDrawingPipeline &&
    prevProps.isDrawingRoad === nextProps.isDrawingRoad &&
    prevProps.isPlacingSensor === nextProps.isPlacingSensor &&
    prevProps.isInspectingClearance === nextProps.isInspectingClearance &&
    prevProps.clearanceWorkOrderId === nextProps.clearanceWorkOrderId &&
    prevProps.clearanceDefaultDepthM === nextProps.clearanceDefaultDepthM &&
    prevProps.selectedAsset?.id === nextProps.selectedAsset?.id &&
    prevProps.selectedSensor?.id === nextProps.selectedSensor?.id &&
    prevProps.selectedPipeline?.id === nextProps.selectedPipeline?.id &&
    prevProps.showAlerts === nextProps.showAlerts &&
    prevProps.readings === nextProps.readings &&                         
    prevProps.sensorPipelineLinks === nextProps.sensorPipelineLinks &&   
    prevCenter[0] === nextCenter[0] &&
    prevCenter[1] === nextCenter[1] &&
    prevProps.mapZoom === nextProps.mapZoom &&
    // --- UPDATE (GIS map — road segment health layer)
    prevProps.roadSegments === nextProps.roadSegments &&
    prevProps.roadDisplayMode === nextProps.roadDisplayMode &&
    prevProps.segmentBands === nextProps.segmentBands &&
    prevProps.showSegmentLabels === nextProps.showSegmentLabels &&
    prevProps.selectedSegmentId === nextProps.selectedSegmentId &&
    prevProps.onSegmentSelect === nextProps.onSegmentSelect &&
    prevProps.onOpenRoadReport === nextProps.onOpenRoadReport &&
    prevProps.showAssetMarkers === nextProps.showAssetMarkers &&
    prevProps.showSensorMarkers === nextProps.showSensorMarkers &&
    prevProps.showAnomalies === nextProps.showAnomalies &&
    // --- END UPDATE
    // --- UPDATE (roads v1.4)
    prevProps.pondingForecast === nextProps.pondingForecast &&
    prevProps.showPonding === nextProps.showPonding &&
    prevProps.pondingHour === nextProps.pondingHour &&
    prevProps.selectedPondingSegmentId === nextProps.selectedPondingSegmentId &&
    prevProps.onPondingSegmentSelect === nextProps.onPondingSegmentSelect &&
    prevProps.onPondingOpenReport === nextProps.onPondingOpenReport &&
    prevProps.onMapReady === nextProps.onMapReady &&
    // --- END UPDATE
    // --- UPDATE (Public Utilities v3)
    prevProps.puLayer === nextProps.puLayer &&
    prevProps.puColorBy === nextProps.puColorBy &&
    prevProps.puVisibleTypes === nextProps.puVisibleTypes &&
    prevProps.showPuZones === nextProps.showPuZones &&
    prevProps.selectedPuId === nextProps.selectedPuId &&
    prevProps.onPuFeatureSelect === nextProps.onPuFeatureSelect &&
    prevProps.showPuLegend === nextProps.showPuLegend &&
    prevProps.liveConnected === nextProps.liveConnected
    // --- END UPDATE
  );
});